// @flow

import './PlaybackTimer.css';

import React from 'react';
import { toSMPTE, toFrames, parsePlaybackTime, isCompareViewMode, toMSS } from '../util/VideoUtil';
import { KeyCode } from '../util/Constants';
import VideoController from '../video/VideoController';

type Props = {
  duration: number,
  progress: number,
  videoFrameRate: number,
  controller: VideoController
};

type State = {
  timeCodeType: 'SMPTE' | 'FRAMES',
  editMode: boolean,
  value: string
};

export default class PlaybackTimer extends React.Component<Props, State> {
  _inputNode: ?HTMLInputElement;

  static defaultProps = {
    duration: 0,
    progress: 0,
    videoFrameRate: 0
  };

  state = {
    timeCodeType: 'SMPTE',
    editMode: false,
    value: '',
    compareViewMode: isCompareViewMode()
  };

  changeTimeCodeType = () => {
    if (this.state.timeCodeType == 'SMPTE') {
      this.setState({
        timeCodeType: 'FRAMES'
      });
    } else {
      this.setState({
        timeCodeType: 'SMPTE'
      });
    }
  };

  setTimeCodeType = () => {
    if (this.props.videoFrameRate > 0) {
      this.changeTimeCodeType();
    }
  };

  onClick = (e: MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      this.setTimeCodeType();
    } else if (!this.state.editMode) {
      const { videoFrameRate } = this.props;
      const progress = this.props.progress * 1000;
      this.setState({ editMode: true, value: toSMPTE(progress, videoFrameRate) }, () => {
        if (this._inputNode) {
          this._inputNode.focus();
          //$FlowFixMe
          this._inputNode.setSelectionRange(0, this._inputNode.value.length);
        }
      });
    }
  };

  onChange = (e: Event) => {
    //$FlowIgnore
    var patt = new RegExp('^[0-9 |:]+$');
    if (patt.test(e.target.value)) {
      this.setState({ value: e.target.value });
    }
  };

  getFullValue = () => {
    const { progress, videoFrameRate } = this.props;
    const { value } = this.state;
    let prefix = '';
    if (/[+-]/.test(value[0])) {
      prefix = toSMPTE(progress * 1000, videoFrameRate);
    }

    const fullValue = `${prefix}${value}`;
    return fullValue;
  };

  onBlur = () => {
    const { videoFrameRate, controller } = this.props;
    const currentTime = parsePlaybackTime(this.getFullValue(), videoFrameRate);
    if (currentTime != null) {
      controller.setCurrentTime(currentTime, true);
    }
    this.setState({ editMode: false });
  };

  onKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === KeyCode.ENTER) {
      this.onBlur();
    }
  };

  render() {
    let { videoFrameRate, progress, duration } = this.props;
    const { timeCodeType, editMode, value, compareViewMode } = this.state;
    const startTimecode: number = this.props.controller.videoStartTimecode;
    if (startTimecode > 0) {
      progress = progress + startTimecode / 1000;
    }

    const pFrames = Math.round(progress * videoFrameRate);
    const dFrames = Math.round(duration * videoFrameRate);

    const pWidth = timeCodeType === 'FRAMES' ? String(pFrames).length * 9 : '';
    const dWidth = timeCodeType === 'FRAMES' ? String(dFrames).length * 9 : '';

    return editMode ? (
      <div name="PlaybackTimer" className="editmode">
        <input
          type="text"
          value={value}
          onChange={this.onChange}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          ref={(node) => (this._inputNode = node)}
        />
      </div>
    ) : (
      <div
        name="PlaybackTimer"
        className={`${timeCodeType.toLowerCase()}${videoFrameRate ? ' has-frame-rate' : ''}`}
        onClick={this.onClick}>
        {compareViewMode ? (
          <div>
            <span className="current" style={{ width: pWidth }}>
              {toMSS(progress * 1000, videoFrameRate)}
            </span>
            <span className="divider">•</span>
            <span className="total" style={{ width: dWidth }}>
              {toMSS(duration * 1000, videoFrameRate)}
            </span>
          </div>
        ) : (
          <div>
            <span className="current" style={{ width: pWidth }}>
              {timeCodeType === 'SMPTE' ? toSMPTE(progress * 1000, videoFrameRate) : pFrames}
            </span>
            <span className="divider">•</span>
            <span className="total" style={{ width: dWidth }}>
              {timeCodeType === 'SMPTE' ? toSMPTE(duration * 1000, videoFrameRate) : dFrames}
            </span>
          </div>
        )}
      </div>
    );
  }
}
